<script setup lang="ts">
import ServiceGuide from './ServiceGuide.vue'
import exportIcon from '~/assets/images/user/vip/icon-export.png'
import collectIcon from '~/assets/images/user/vip/icon-collect.png'
import filterIcon from '~/assets/images/user/vip/icon-filter.png'
import rankingIcon from '~/assets/images/user/vip/icon-rank.png'
import listIcon from '~/assets/images/user/vip/icon-list.png'
import createSearch from '~/assets/images/user/vip/icon-search.png'
import detailIcon from '~/assets/images/user/vip/icon-detail.png'
import bloggerContactIcon from '~/assets/images/user/vip/icon-blogger-cantact.png'
import marketingIcon from '~/assets/images/user/vip/icon-marketing.png'
import creativeIcon from '~/assets/images/user/vip/icon-creative.png'
import analysisIcon from '~/assets/images/user/vip/icon-analysis.png'
import overviewIcon from '~/assets/images/user/vip/icon-overview.png'

const props = defineProps<{
  canClose?: boolean
  expriedType?: number
}>()
const emits = defineEmits(['close'])
const { expriedType } = useExpired()
watch(() => props.expriedType, (val) => {
  if (val) {
    expriedType.value = val
  }
}, { immediate: true })
const { userRole } = useLogin()
const localePath = useLocalePath()
const { t, locale } = useI18n()
const getUpgradeResource = computed(() => {
  if ([4, 7, 8, 9].includes(expriedType.value)) {
    return { resource: 1, resourceName: '企业版', limit: 2000, key: 'enterprise' }
  }
  switch (userRole.value?.resource) {
    case 3:
      return { resource: 2, resourceName: '专业版', limit: 10, key: 'standard' }
    case 2:
      return { resource: 1, resourceName: '企业版', limit: 2000, key: 'enterprise' }
    default:
      return { resource: 1, resourceName: '企业版', limit: 2000, key: 'enterprise' }
  }
})
function toPay() {
  navigateTo(localePath(`/vip?type=${getUpgradeResource.value?.resource}`))
  emits('close')
}

function sortItemsByNames(items: any[], names?: string[]): any[] {
  if (!names)
    return items

  const nameIndexes = new Map()
  names.forEach((name, index) => {
    nameIndexes.set(t(name), index)
  })

  return items.sort((a, b) => {
    const indexA = nameIndexes.get(a.name) ?? -1
    const indexB = nameIndexes.get(b.name) ?? -1
    if (indexA === -1)
      return 1
    if (indexB === -1)
      return -1
    return indexA - indexB
  })
}
function getBasePrivilegeItems(names?: string[]) {
  const isResource2 = getUpgradeResource.value?.resource === 2
  const baseItems = isResource2
    ? [
        { icon: collectIcon, name: t('我的收藏'), desc: t('vip.upTo1', [600]) },
        { icon: filterIcon, name: t('高级筛选'), desc: t('vip.不限') },
        { icon: exportIcon, name: t('数据导出'), desc: `100${t('次/月')}` },
        { icon: rankingIcon, name: t('榜单数据'), desc: t('vip.top', [500]) },
      ]
    : getBasePrivilegeItemsCoper()
  return sortItemsByNames(baseItems, names)
}
function getBasePrivilegeItemsCoper() {
  const isZh = locale.value === 'zh'
  return [
    { icon: exportIcon, name: t('数据导出'), desc: `500${t('次/月')}` },
    { icon: bloggerContactIcon, name: t('达人邮箱导出'), desc: `5${t('次/天')}` },
    isZh && { icon: marketingIcon, name: t('市场大盘'), desc: t('免费体验') },
    isZh && { icon: creativeIcon, name: t('创意选品'), desc: t('免费体验') },
    { icon: listIcon, name: t('列表数据'), desc: t('vip.不限') },
    { icon: collectIcon, name: t('我的收藏'), desc: t('vip.upTo1', [2000]) },
  ].filter(Boolean)
}
function getMarketingPrivilegeItems(names?: string[]) {
  const items = [
    { icon: analysisIcon, name: t('行业分析'), desc: t('vip.不限') },
    { icon: overviewIcon, name: t('大盘概览'), desc: t('vip.不限') },
    { icon: exportIcon, name: t('数据导出'), desc: `2000${t('次/月')}` },
    { icon: bloggerContactIcon, name: t('达人邮箱导出'), desc: `5${t('次/天')}` },
    { icon: creativeIcon, name: t('创意选品'), desc: t('vip.不限') },
  ]
  return sortItemsByNames(items, names)
}
// 弹窗类型，1代表普通，2代表收藏，3代表导出，4代表达人导出 5代表详情次数 6：搜索次数 7：市场大盘 8：行业分析
const privilegeItemsMaps = computed<Record<number, any>>(() => {
  const isResource2 = getUpgradeResource.value?.resource === 2
  return {
    1: isResource2 ? getBasePrivilegeItems(['我的收藏', '列表数据']) : getBasePrivilegeItems(['市场大盘', '创意选品', '列表数据', '数据导出']),
    2: getBasePrivilegeItems(['我的收藏', '市场大盘', '创意选品', '列表数据']),
    3: getBasePrivilegeItems(['数据导出']),
    4: getBasePrivilegeItems(['数据导出']),
    5: [{ icon: detailIcon, name: t('查看详情'), desc: '90天数据' }, ...getBasePrivilegeItems(['数据导出']).slice(0, 3)],
    6: [{ icon: createSearch, name: t('搜索次数'), desc: '不限' }, { icon: detailIcon, name: t('查看详情'), desc: '90天数据' }, ...getBasePrivilegeItems(['我的收藏']).slice(0, 2)],
    7: getMarketingPrivilegeItems(['大盘概览']),
    8: getMarketingPrivilegeItems(['行业分析']),
    9: getMarketingPrivilegeItems(['创意选品']),
  }
})
const privilege = computed(() => {
  return (privilegeItemsMaps.value[expriedType.value] || getBasePrivilegeItems(['我的收藏', '列表数据']))?.slice(0, 4)
})

const title = computed(() => {
  const isResource3 = userRole.value?.resource === 3
  const tip = isResource3 ? '开通' : '升级'
  const resourceName = t(getUpgradeResource.value?.resourceName || '')

  const defaultTip = t('vip.normalModal', [resourceName, tip])
  const messages: Record<number, string> = {
    1: defaultTip,
    2: t('vip.favouriteModal', [resourceName, tip]),
    3: t('vip.exportModal', [resourceName, tip]),
    4: t('vip.exportCreatorModal', [t('企业版'), tip]),
    5: t('您今日的详情查看次数已用完，开通专业版享更多权益'),
    6: t('您今日的搜索次数已用完，开通专业版享更多权益'),
    7: t('vip.exportMarketOverviewModal', [t('企业版'), tip]),
    8: t('vip.exportMarketAnalysisModal', [t('企业版'), tip]),
    9: t('vip.exportMarketCreativeModal', [t('企业版'), tip]),
  }

  return messages[expriedType.value] || defaultTip
})
// 是否显示客服二维码
const showServiceGuide = computed(() => {
  return [7, 8, 9].includes(expriedType.value) && locale.value === 'zh'
})
const btnLabel = computed(() => {
  if (showServiceGuide.value)
    return t('直接升级企业会员')
  return userRole?.value?.resource === 3 ? t(`开通${getUpgradeResource.value?.resourceName}`) : t(`升级${getUpgradeResource.value?.resourceName}`)
})
const { isBrHost } = useJudgeBrHost()

function toBrPay() {
  navigateTo(localePath('/pay/pt'))
  emits('close')
}
</script>

<template>
  <div class="g-expired-dialog__content relative relative h-fit flex flex-col items-center justify-center gap-24 px-50 py-24 pb-30">
    <div
      v-if="canClose"
      class="absolute right-16 top-16 h-28 w-28 flex cursor-pointer items-center justify-center text-20 hover:op-80"
      @click="emits('close')"
    >
      <i class="icon-shanchu" />
    </div>
    <template v-if="isBrHost && [2, 3].includes(userRole?.resource)">
      <div class="text-16 text-[--color-text-1] font-500">
        {{ $t('开通会员') }}
      </div>
      <div lass="text-14 text-[--color-text-1] ">
        {{ $t('解锁更多数据内容，升级会员享更多权益') }}
      </div>
      <div>
        <UserVipAsideContent
          :privilege="privilege"
          class="overflow-hidden !w-500"
          :resource="getUpgradeResource?.resource"
          isHideTip
        />
      </div>
      <div item-center mx-auto w-388 flex justify-center>
        <UserVipBtn
          :payType="1"
          :data="getUpgradeResource"
          :label="$t('升级会员')"
          @click="toBrPay"
        />
      </div>
    </template>
    <template v-else>
      <div class="text-16 text-[--color-text-1] font-500">
        {{ userRole?.resource === 3 ? $t('开通会员') : $t('升级提醒') }}
      </div>
      <div lass="text-14 text-[--color-text-1] ">
        {{ title }}
      </div>
      <div>
        <UserVipAsideContent
          :privilege="privilege"
          class="overflow-hidden !w-500"
          :resource="getUpgradeResource?.resource"
        />
      </div>
      <div item-center mx-auto w-388 flex justify-center>
        <UserVipBtn
          :class="showServiceGuide ? '!max-w-full w-full' : ''"
          :payType="1"
          :data="getUpgradeResource"
          :label="btnLabel"
          @click="toPay"
        />
      </div>
      <!-- <ServiceGuide v-if="showServiceGuide" /> -->
    </template>
  </div>
</template>

<style lang="less">
.g-expired-dialog__content {
  border-radius: 12px;
  background: linear-gradient(185deg, #FEF7F7 -15.28%, #F4F3FF 49.52%, #F5F3FF 52.41%, #FFF 93.6%);
}
</style>
